/*!
 * @pixi/canvas-prepare - v6.5.9
 * Compiled Wed, 25 Jan 2023 05:01:45 UTC
 *
 * @pixi/canvas-prepare is licensed under the MIT License.
 * http://www.opensource.org/licenses/mit-license
 */
import { ExtensionType, BaseTexture } from '@pixi/core';
import { BasePrepare } from '@pixi/prepare';
import { settings } from '@pixi/settings';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) { if (b.hasOwnProperty(p)) { d[p] = b[p]; } } };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var CANVAS_START_SIZE = 16;
/**
 * Built-in hook to upload PIXI.Texture objects to the GPU.
 * @private
 * @param prepare - Instance of CanvasPrepare
 * @param item - Item to check
 * @returns If item was uploaded.
 */
function uploadBaseTextures(prepare, item) {
    var tempPrepare = prepare;
    if (item instanceof BaseTexture) {
        var image = item.source;
        // Sometimes images (like atlas images) report a size of zero, causing errors on windows phone.
        // So if the width or height is equal to zero then use the canvas size
        // Otherwise use whatever is smaller, the image dimensions or the canvas dimensions.
        var imageWidth = image.width === 0 ? tempPrepare.canvas.width : Math.min(tempPrepare.canvas.width, image.width);
        var imageHeight = image.height === 0 ? tempPrepare.canvas.height
            : Math.min(tempPrepare.canvas.height, image.height);
        // Only a small subsections is required to be drawn to have the whole texture uploaded to the GPU
        // A smaller draw can be faster.
        tempPrepare.ctx.drawImage(image, 0, 0, imageWidth, imageHeight, 0, 0, tempPrepare.canvas.width, tempPrepare.canvas.height);
        return true;
    }
    return false;
}
/**
 * The prepare manager provides functionality to upload content to the GPU.
 *
 * This cannot be done directly for Canvas like in WebGL, but the effect can be achieved by drawing
 * textures to an offline canvas. This draw call will force the texture to be moved onto the GPU.
 *
 * An instance of this class is automatically created by default, and can be found at `renderer.plugins.prepare`
 * @class
 * @extends PIXI.BasePrepare
 * @memberof PIXI
 */
var CanvasPrepare = /** @class */ (function (_super) {
    __extends(CanvasPrepare, _super);
    /**
     * @param renderer - A reference to the current renderer
     */
    function CanvasPrepare(renderer) {
        var _this = _super.call(this, renderer) || this;
        _this.uploadHookHelper = _this;
        _this.canvas = settings.ADAPTER.createCanvas(CANVAS_START_SIZE, CANVAS_START_SIZE);
        _this.ctx = _this.canvas.getContext('2d');
        // Add textures to upload
        _this.registerUploadHook(uploadBaseTextures);
        return _this;
    }
    /** Destroys the plugin, don't use after this */
    CanvasPrepare.prototype.destroy = function () {
        _super.prototype.destroy.call(this);
        this.ctx = null;
        this.canvas = null;
    };
    /** @ignore */
    CanvasPrepare.extension = {
        name: 'prepare',
        type: ExtensionType.CanvasRendererPlugin,
    };
    return CanvasPrepare;
}(BasePrepare));

export { CanvasPrepare };
//# sourceMappingURL=canvas-prepare.mjs.map
